/**
 * 编辑器教学引导区域
 * 淡蓝色背景，hover 弹出浮窗播放教学 GIF
 * 支持"不再提醒"（持久化到数据库）和"知道了"（仅本次会话）
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getToken } from '../../services/api';
import { useLanguage } from '../../i18nContext';

type DismissState = 'active' | 'session-dismissed' | 'permanent-dismissed';

export function TutorialZone() {
  const { t } = useLanguage();
  const [dismissState, setDismissState] = useState<DismissState>(() => {
    if (sessionStorage.getItem('tutorial-session-dismissed')) return 'session-dismissed';
    return 'active';
  });
  const [showPopover, setShowPopover] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoneRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // 加载用户的 tutorialDismissed 状态
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    fetch('/user/info', {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (data.code === 200 && data.data?.tutorialDismissed === 1) {
          setDismissState('permanent-dismissed');
        }
      })
      .catch(() => {});
  }, []);

  const isDismissed = dismissState !== 'active';

  const handleMouseEnterZone = useCallback(() => {
    if (isDismissed) return;
    hoverTimerRef.current = setTimeout(() => setShowPopover(true), 300);
  }, [isDismissed]);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setShowPopover(false), 150);
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const handleMouseLeaveZone = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    scheduleClose();
  }, [scheduleClose]);

  const handleMouseEnterPopover = useCallback(() => {
    cancelClose();
  }, [cancelClose]);

  const handleMouseLeavePopover = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const handleDismissSession = useCallback(() => {
    setDismissState('session-dismissed');
    setShowPopover(false);
    sessionStorage.setItem('tutorial-session-dismissed', '1');
  }, []);

  const handleDismissPermanent = useCallback(() => {
    setDismissState('permanent-dismissed');
    setShowPopover(false);
    const token = getToken();
    if (!token) return;
    fetch('/user/dismiss-tutorial', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).catch(() => {});
  }, []);

  return (
    <div
      ref={zoneRef}
      style={{
        width: '40px',
        height: '100%',
        flexShrink: 0,
        background: isDismissed ? '#fff' : '#EBF5FF',
        cursor: isDismissed ? 'default' : 'pointer',
        position: 'relative',
        transition: 'background 0.3s',
      }}
      data-is-background="true"
      onMouseEnter={handleMouseEnterZone}
      onMouseLeave={handleMouseLeaveZone}
    >
      {showPopover && (
        <div
          ref={popoverRef}
          onMouseEnter={handleMouseEnterPopover}
          onMouseLeave={handleMouseLeavePopover}
          style={{
            position: 'absolute',
            left: '44px',
            top: '20px',
            zIndex: 1000,
            background: '#fff',
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
            padding: '16px',
            width: '320px',
          }}
        >
          {/* GIF 教学动画 */}
          <div style={{
            width: '100%',
            height: '180px',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#f0f7ff',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src="/assets/editor-tutorial.gif"
              alt={t.workspace.tutorialAlt}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).parentElement!.innerHTML =
                  '<div style="color:#93c5fd;font-size:13px;text-align:center;padding:20px">Tutorial<br/>(/assets/editor-tutorial.gif)</div>';
              }}
            />
          </div>
          <p style={{ margin: '0 0 12px', fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>
            {t.workspace.tutorialText}
          </p>
          {/* 按钮区 */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleDismissPermanent}
              style={{
                padding: '6px 14px',
                border: '1px solid #e5e7eb',
                background: '#fff',
                color: '#6b7280',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {t.workspace.dontRemind}
            </button>
            <button
              onClick={handleDismissSession}
              style={{
                padding: '6px 14px',
                border: '1px solid #3b82f6',
                background: '#3b82f6',
                color: '#fff',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {t.workspace.gotIt}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TutorialZone;
